import React from "react";
import { toast } from "sonner";
import { Facebook, Twitter, Instagram, MessageCircle, Link2 } from "lucide-react";
import { useLang } from "../i18n/i18n";

export default function SocialShare({ card }) {
  const { t } = useLang();
  const origin = window.location.origin;
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  // Build a share URL that Facebook / iMessage / WhatsApp crawlers can hit
  // to pull dynamic OG tags for the winning restaurant (image, name, price).
  // Falls back to the raw origin when no card is present.
  const shareUrl = card
    ? `${API}/share?${new URLSearchParams({
        id: card.id || "",
        name: card.name || "",
        cuisine: card.cuisine || "",
        price: card.price || "",
        distance: card.distance != null ? String(card.distance) : "",
        image: card.image || "",
      }).toString()}`
    : origin;
  const text = card
    ? `\u2620 The reaper has spoken: ${card.name} (${card.cuisine} \u00b7 ${card.price})${card.distance ? ` — ${card.distance} mi away` : ""}! Deal your own fate on Fork\u00b7Fate:`
    : `Let fate pick your next meal, drink or dessert — shuffle the deck on Fork\u00b7Fate!`;
  const full = `${text} ${shareUrl}`;

  const openShare = (u) =>
    window.open(u, "_blank", "noopener,noreferrer,width=600,height=520");

  const copyFor = async (platform) => {
    try {
      await navigator.clipboard.writeText(full);
      toast.success(`Copied! Paste it into ${platform}.`);
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };

  const links = [
    {
      key: "facebook",
      label: "Facebook",
      Icon: Facebook,
      color: "#1877F2",
      onClick: () => openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(text)}`),
    },
    {
      key: "x",
      label: "X",
      Icon: Twitter,
      color: "#0E0E0E",
      onClick: () => openShare(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`),
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      Icon: MessageCircle,
      color: "#25D366",
      onClick: () => openShare(`https://wa.me/?text=${encodeURIComponent(full)}`),
    },
    {
      key: "instagram",
      label: "Instagram",
      Icon: Instagram,
      color: "#E4405F",
      onClick: () => copyFor("Instagram"),
    },
    {
      key: "copy",
      label: "Copy link",
      Icon: Link2,
      color: "#6B7075",
      onClick: () => copyFor("anywhere"),
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="social-share">
      <span className="font-sans text-xs font-bold uppercase tracking-[0.15em] text-[#6B7075]">{t("Share")}</span>
      {links.map(({ key, label, Icon, color, onClick }) => (
        <button
          key={key}
          onClick={onClick}
          data-testid={`share-${key}`}
          title={`Share on ${label}`}
          aria-label={`Share on ${label}`}
          className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-[#E2E4E7] bg-white transition-transform hover:scale-110 hover:border-transparent hover:text-white"
          style={{ "--hover": color }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = color)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
        >
          <Icon className="h-2.5 w-2.5" style={{ color: "inherit" }} />
        </button>
      ))}
    </div>
  );
}
